<!-- src/apps/secret/components/conceal/landing/TypingLine.vue -->

<script setup lang="ts">
  import { usePreferredReducedMotion } from '@vueuse/core';
  import { onBeforeUnmount, onMounted, ref } from 'vue';

  // Fake-credential samples for the hero terminal line. Deliberately not
  // translated: they read as shell/env content in every locale.
  const PHRASES = [
    'senha_prod_db=K8s!2024#xZ',
    'STRIPE_SECRET_KEY=sk_live_x9',
    'token=eyJhbGciOiJIUzI1NiJ9',
    'ssh-rsa AAAAB3NzaC1yc2E',
  ];

  // Charset for the "encrypted" render — looks like the base64url ciphertext the
  // product actually produces.
  const CIPHER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const randCipher = () => CIPHER.charAt(Math.floor(Math.random() * CIPHER.length));

  const TYPE_MS = 55;
  const HOLD_MS = 1100;
  const SCRAMBLE_MS = 34; // frame time while encrypting
  const LOCKED_MS = 1500;

  const displayed = ref('');
  const blink = ref(true);
  const locked = ref(false); // true once the line is "encrypted" → padlock shown
  const reducedMotion = usePreferredReducedMotion();

  let timer: ReturnType<typeof setTimeout> | null = null;
  let blinkTimer: ReturnType<typeof setInterval> | null = null;

  // Encrypt sweep: the locked prefix grows left→right while the tail keeps
  // scrambling — reads as "the secret is being encrypted, character by character".
  const encrypt = (phraseIdx: number) => {
    const len = Math.min(PHRASES[phraseIdx].length, 26);
    const final = Array.from({ length: len }, randCipher);

    // Reduced motion: skip the flickery scramble — jump straight to the locked
    // ciphertext, but keep the phrase rotation going (a gentle text change).
    if (reducedMotion.value === 'reduce') {
      displayed.value = final.join('');
      locked.value = true;
      timer = setTimeout(() => next(phraseIdx), LOCKED_MS);
      return;
    }

    const frame = (k: number, tick: number) => {
      const head = final.slice(0, k).join('');
      const tail = Array.from({ length: len - k }, randCipher).join('');
      displayed.value = head + tail;
      if (k < len) {
        // advance the locked prefix every other frame for a visible sweep
        timer = setTimeout(() => frame(tick % 2 === 1 ? k + 1 : k, tick + 1), SCRAMBLE_MS);
      } else {
        locked.value = true;
        timer = setTimeout(() => next(phraseIdx), LOCKED_MS);
      }
    };
    frame(0, 0);
  };

  const next = (phraseIdx: number) => {
    locked.value = false;
    displayed.value = '';
    type((phraseIdx + 1) % PHRASES.length, 0);
  };

  const type = (phraseIdx: number, len: number) => {
    const phrase = PHRASES[phraseIdx];
    displayed.value = phrase.slice(0, len);
    if (len < phrase.length) {
      timer = setTimeout(() => type(phraseIdx, len + 1), TYPE_MS);
    } else {
      timer = setTimeout(() => encrypt(phraseIdx), HOLD_MS);
    }
  };

  onMounted(() => {
    // The rotation (typing + encrypt→lock cycle) always runs — it's a gentle,
    // decorative text change. Only the flickery scramble (handled in encrypt)
    // and the blinking cursor are gated on prefers-reduced-motion.
    type(0, 0);
    if (reducedMotion.value !== 'reduce') {
      blinkTimer = setInterval(() => (blink.value = !blink.value), 530);
    }
  });

  onBeforeUnmount(() => {
    if (timer) clearTimeout(timer);
    if (blinkTimer) clearInterval(blinkTimer);
  });
</script>

<template>
  <div
    class="flex items-center gap-2.5 border px-4 py-3 transition-colors duration-300"
    :class="
      locked
        ? 'border-brand-500/60 bg-day dark:border-brand-400/50 dark:bg-night-100'
        : 'border-day-line-2 bg-day dark:border-white/[0.13] dark:bg-night-100'
    "
    aria-hidden="true">
    <!-- prompt → padlock -->
    <span
      class="shrink-0"
      :class="locked ? 'text-brand-600 dark:text-brand-400' : 'text-brand-500'">
      <svg
        v-if="locked"
        class="size-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round">
        <rect
          x="4"
          y="11"
          width="16"
          height="9"
          rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
      <span
        v-else
        class="font-mono text-[10px]">$_</span>
    </span>

    <!-- the line: plaintext (muted) while typing, ciphertext (brand) once encrypted -->
    <span
      class="min-w-0 truncate font-mono text-[13px] transition-colors duration-300"
      :class="
        locked
          ? 'text-brand-700 dark:text-brand-300'
          : 'text-day-ink-muted dark:text-night-ink-muted'
      ">
      {{ displayed
      }}<span
        v-if="!locked"
        class="text-brand-500 transition-opacity duration-100"
        :class="blink ? 'opacity-100' : 'opacity-0'">█</span>
    </span>

    <!-- state tag -->
    <span
      class="ml-auto shrink-0 font-mono text-[9px] tracking-[0.1em] uppercase transition-colors duration-300"
      :class="
        locked
          ? 'text-brand-600 dark:text-brand-400'
          : 'text-day-ink-faint dark:text-night-ink-faint'
      ">
      {{ locked ? 'cifrado' : 'texto puro' }}
    </span>
  </div>
</template>
